export enum WorkspaceIcon {
    Square = "Square",
    Triangle = "Triangle",
    ArrowUpRight = "ArrowUpRight",
    Target = "Target",
    RightArrow = "RightArrow",
    Circle = "Circle",
    Diamond = "Diamond",
    Hexagon = "Hexagon",
    Grid = "Grid",
    Layers = "Layers",
    Bolt = "Bolt",
    Cloud = "Cloud",
    Plus = "Plus",
    Folder = "Folder",
    Tag = "Tag",
    Globe = "Globe",
    Database = "Database",
    Chart = "Chart",
    Link = "Link",
    Shield = "Shield",
    Star = "Star",
    Document = "Document",
}

interface WorkspaceBase {
    id: number;
    isDefault: boolean;
    slug: string;
    displayName: string;
    summary: string | null;
    icon: WorkspaceIcon;
}

export type WorkspaceListItem = WorkspaceBase;

// Slugs are user-defined and could otherwise collide with unrelated ids elsewhere on the page
// (e.g. the shared layout's "navigationbar"), so card/banner elements use this namespaced id.
export const workspaceCardElementId = (slug: string) => `workspace-card-${slug}`;

export interface CreateWorkspaceRequest {
    slug: string;
    displayName: string;
    summary: string | null;
    icon: WorkspaceIcon;
}

export interface UpdateWorkspaceRequest {
    slug: string;
    displayName: string;
    summary: string | null;
    icon: WorkspaceIcon;
}

export interface WorkspaceResponse extends WorkspaceBase {
    documentation?: string | null;
}

export interface WorkspaceSummaryResponse {
    displayName: string;
    icon: WorkspaceIcon;
}

export interface WorkspaceArtefactCounts {
    processesCount: number;
    subprocessesCount: number;
    environmentVariablesCount: number;
    apiSpecsCount: number;
    templatesCount: number;
}

export enum ArtefactType {
    Process = "Process",
    Subprocess = "Subprocess",
    EnvironmentVariable = "EnvironmentVariable",
    Api = "Api",
    ProcessTemplate = "Template",
}

export interface WorkspaceArtefact {
    artefactId: number;
    artefactGuid: string;
    type: ArtefactType;
    name: string;
    artefactModifiedUtc: string;
    artefactModifiedBy: string;
}

export interface WorkspaceArtefactSummary {
    artefactGuid: string;
    type: ArtefactType;
}

export interface WorkspaceArtefactAssignmentWriteRequest {
    artefacts: WorkspaceArtefactSummary[];
    workspaceIdsToAssign: number[];
}

export interface WorkspaceArtefactAssignments {
    workspaceIds: number[];
}

export enum WorkspaceAccessLevel {
    User = "User",
    Admin = "Admin",
}

export interface WorkspaceRoleMember {
    userId: string;
    userName: string;
}

export interface WorkspaceRoleAccess {
    roleId: string;
    roleName: string;
    accessLevel: WorkspaceAccessLevel;
    members: WorkspaceRoleMember[];
}

export interface WorkspaceUserAccess {
    userId: string;
    userName: string;
    accessLevel: WorkspaceAccessLevel;
}

export interface WorkspaceAccessResponse {
    userRoles: WorkspaceRoleAccess[];
    users: WorkspaceUserAccess[];
}

export interface WorkspaceMember {
    userId: string;
    userName: string;
    effectiveLevel: WorkspaceAccessLevel;
    // Only direct grants can be deleted. Toggling a role-only member creates a direct grant that
    // overrides inherited role access.
    hasDirectGrant: boolean;
    grantingRoleNames: string[];
    // Whether any granting role is Admin-level - if so, revoking a direct grant alone would leave
    // the member Admin via the role, so demoting them requires a direct User-level override instead.
    hasAdminGrantingRole: boolean;
}

const isHigherLevel = (a: WorkspaceAccessLevel, b: WorkspaceAccessLevel) =>
    a === WorkspaceAccessLevel.Admin && b !== WorkspaceAccessLevel.Admin;

export const resolveWorkspaceMembers = (access: WorkspaceAccessResponse): WorkspaceMember[] => {
    const membersByUserId = new Map<string, WorkspaceMember>();

    access.users.forEach((user) => {
        membersByUserId.set(user.userId, {
            userId: user.userId,
            userName: user.userName,
            effectiveLevel: user.accessLevel,
            hasDirectGrant: true,
            grantingRoleNames: [],
            hasAdminGrantingRole: false
        });
    });

    access.userRoles.forEach((role) => {
        role.members.forEach((member) => {
            const existing = membersByUserId.get(member.userId);
            if (existing) {
                existing.grantingRoleNames.push(role.roleName);
                if (role.accessLevel === WorkspaceAccessLevel.Admin) {
                    existing.hasAdminGrantingRole = true;
                }
                // A direct grant's level always wins; for role-only members, take the highest
                // level among their granting roles.
                if (!existing.hasDirectGrant && isHigherLevel(role.accessLevel, existing.effectiveLevel)) {
                    existing.effectiveLevel = role.accessLevel;
                }
                return;
            }

            membersByUserId.set(member.userId, {
                userId: member.userId,
                userName: member.userName,
                effectiveLevel: role.accessLevel,
                hasDirectGrant: false,
                grantingRoleNames: [role.roleName],
                hasAdminGrantingRole: role.accessLevel === WorkspaceAccessLevel.Admin
            });
        });
    });

    // Admins first, then alphabetically by userName within each level.
    return Array.from(membersByUserId.values()).sort((a, b) => {
        if (a.effectiveLevel !== b.effectiveLevel) {
            return a.effectiveLevel === WorkspaceAccessLevel.Admin ? -1 : 1;
        }
        return a.userName.localeCompare(b.userName);
    });
};

// AspNetUsers.Id / AspNetRoles.Id
export interface WorkspaceAccessGrant {
    id: string;
    accessLevel: WorkspaceAccessLevel;
}

export interface WorkspaceAccessGrantRequest {
    userRoles: WorkspaceAccessGrant[];
    users: WorkspaceAccessGrant[];
}

export interface WorkspaceAccessWriteRequest {
    userRoleIds: string[];
    userIds: string[];
}
